import { supabase } from '@/lib/supabase';
import type { StorageApiErrorPayload } from './types';

export class WerseeStorageError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public requestId?: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export const storageAuthHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new WerseeStorageError('AUTH_REQUIRED', 'Sign in to use Wersee storage.', 401);
  return { Authorization: `Bearer ${token}` };
};

export const storageApi = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = await storageAuthHeaders();
  const response = await fetch(`/api/storage${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as StorageApiErrorPayload | null;
    throw new WerseeStorageError(
      payload?.error?.code || `STORAGE_HTTP_${response.status}`,
      payload?.error?.message || 'Wersee storage could not complete this request.',
      response.status,
      payload?.error?.requestId,
      payload?.error?.details,
    );
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
};

export const storageApiUpload = async <T>(
  path: string,
  init: RequestInit,
  onUploadProgress: (transferredBytes: number) => void,
): Promise<T> => {
  const authHeaders = await storageAuthHeaders();
  const requestHeaders = new Headers(init.headers);
  Object.entries(authHeaders).forEach(([name, value]) => requestHeaders.set(name, value));

  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    const cleanup = () => init.signal?.removeEventListener('abort', abort);

    request.open(init.method || 'GET', `/api/storage${path}`);
    requestHeaders.forEach((value, name) => request.setRequestHeader(name, value));
    request.upload.onprogress = (event) => onUploadProgress(event.loaded);
    request.onerror = () => {
      cleanup();
      reject(new TypeError('Wersee storage upload could not reach the server.'));
    };
    request.onabort = () => {
      cleanup();
      reject(new DOMException('Upload aborted.', 'AbortError'));
    };
    request.onload = () => {
      cleanup();
      const payload = request.responseText
        ? (() => {
            try {
              return JSON.parse(request.responseText) as T | StorageApiErrorPayload;
            } catch {
              return null;
            }
          })()
        : null;
      if (request.status < 200 || request.status >= 300) {
        const errorPayload = payload as StorageApiErrorPayload | null;
        reject(new WerseeStorageError(
          errorPayload?.error?.code || `STORAGE_HTTP_${request.status}`,
          errorPayload?.error?.message || 'Wersee storage could not complete this request.',
          request.status,
          errorPayload?.error?.requestId,
          errorPayload?.error?.details,
        ));
        return;
      }
      resolve((request.status === 204 ? undefined : payload) as T);
    };

    if (init.signal?.aborted) {
      reject(new DOMException('Upload aborted.', 'AbortError'));
      return;
    }
    init.signal?.addEventListener('abort', abort, { once: true });
    request.send((init.body ?? null) as XMLHttpRequestBodyInit | null);
  });
};

export const isTemporaryStorageFailure = (error: unknown) =>
  error instanceof TypeError
  || (
    error instanceof WerseeStorageError
    && (error.status === 429 || error.status >= 500)
  );
